import { Box, Breadcrumbs, Button, Chip, Link } from '@mui/material';
import { observer } from 'mobx-react-lite';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { SiddurNode } from '@shared/types';
import { useStoreByName } from '../../../hooks/use-stores.hooks';

/**
 * Navigation that follows the tree instead of assuming its shape.
 *
 * The Siddur used to be five fixed dropdowns, so a prayer four levels deep and
 * a prayer six levels deep had to be forced into the same five slots. This
 * walks whatever depth the data actually has: the crumbs are the path taken,
 * the chips are wherever the reader can go next, and a leaf simply offers no
 * chips.
 */
const SiddurBreadcrumb = () => {
  const { path, childBranches, goToCrumb, goToChild } = useStoreByName('siddurStore');

  const label = (node: SiddurNode) => node.name_he || node.name_en || '';

  // dir as an attribute, not a style: the app flips MUI styles through stylis
  // when the interface is Hebrew, so anything written as `right` here would be
  // turned into `left` in exactly the case we want right. The Siddur is Hebrew
  // whatever language the buttons are in, so its navigation starts on the
  // right either way, and the chips wrap from the right rather than the left.
  return (
    <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, md: 1.5 }, mb: { xs: 0.5, md: 2 } }}>
      <Breadcrumbs separator={<NavigateBeforeIcon fontSize="small" />} aria-label="ניווט בסידור">
        {path.map((node: SiddurNode, depth: number) =>
          depth === path.length - 1 ? (
            <span key={node.id} className="OpenFrankSiddur" style={{ fontWeight: 600, color: '#1a1a1a' }}>
              {label(node)}
            </span>
          ) : (
            <Link
              key={node.id}
              component="button"
              underline="hover"
              color="inherit"
              className="OpenFrankSiddur"
              onClick={() => goToCrumb(depth)}
            >
              {label(node)}
            </Link>
          )
        )}
      </Breadcrumbs>

      {/* On a phone the chips are one row that scrolls sideways, so the
          words keep the screen; on a desktop they wrap and are all in view. */}
      {childBranches.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            gap: 1,
            pb: { xs: 0.5, md: 0 },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {childBranches.map((child: SiddurNode) => (
            <Chip
              key={child.id}
              label={label(child)}
              onClick={() => goToChild(child)}
              variant="outlined"
              className="OpenFrankSiddur"
              sx={{ borderRadius: '12px', flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      {path.length > 1 && (
        <Button
          size="small"
          sx={{ alignSelf: 'flex-start', display: { xs: 'none', md: 'inline-flex' } }}
          onClick={() => goToCrumb(path.length - 2)}
        >
          חזרה
        </Button>
      )}
    </Box>
  );
};

export default observer(SiddurBreadcrumb);
